import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MRT_Row, MRT_TableOptions, MRT_SortingState } from 'material-react-table';
import { ContractZoneDto } from 'src/generated/services/TFinancialApi';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useUpsertContractZone, useDeleteContractZone } from 'src/use-cases/settings/useContracts';
import { openToast } from 'src/use-cases/toast/useToast';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';

interface ZoneDetailsTableProps {
  contractId: string;
  zones: ContractZoneDto[];
}

export const ZoneDetailsTable = forwardRef<any, ZoneDetailsTableProps>(({ contractId, zones }, ref) => {
  const [rowToDelete, setRowToDelete] = useState<ContractZoneDto | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isMutating, setIsMutating] = useState(false);

  // Default sort by creation date descending
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'createdOn',
      desc: true,
    },
  ]);

  const { mutateAsync: deleteZone, isLoading: isDeleting } = useDeleteContractZone();
  const { mutateAsync: upsertZone } = useUpsertContractZone();

  // Helper function to clear validation errors
  const clearValidationError = useCallback((fieldName: string) => {
    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  }, []);

  // Validate zone fields
  const onValidateZone = useCallback((zone: Record<string, any>) => {
    const validationResult = {
      location: !zone.location || zone.location.trim() === '' ? 'Location is required' : '',
      zoneCode: !zone.zoneCode || zone.zoneCode.trim() === '' ? 'Zone Code is required' : '',
    };
    return validationResult;
  }, []);

  // CREATE action
  const handleCreateZone = async ({ values, exitCreatingMode }: Parameters<NonNullable<MRT_TableOptions<ContractZoneDto>['onCreatingRowSave']>>[0]) => {
    const newValidationErrors = onValidateZone(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      await upsertZone({
        contractId,
        location: values.location,
        zoneCode: values.zoneCode,
      });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Zone created successfully!',
      });
      exitCreatingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };

  // UPDATE action
  const handleUpdateZone = async ({ row, values, exitEditingMode }: Parameters<NonNullable<MRT_TableOptions<ContractZoneDto>['onEditingRowSave']>>[0]) => {
    const newValidationErrors = onValidateZone(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    try {
      setIsMutating(true);

      await upsertZone({
        id: row.original.id,
        contractId,
        location: values.location,
        zoneCode: values.zoneCode,
      });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Zone updated successfully!',
      });
      exitEditingMode();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    } finally {
      setIsMutating(false);
    }
  };

  // DELETE action
  const handleDeleteZone = async (zone: ContractZoneDto) => {
    try {
      await deleteZone({ contractZoneId: zone.id!, contractId });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Zone deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  // Render row actions
  const renderRowActions = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, react/prop-types
    ({ row, table }: { row: MRT_Row<ContractZoneDto>; table: any }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  // Define columns for the Zone table
  const zoneColumns = useMemo<MRT_ColumnDef<ContractZoneDto>[]>(
    () => [
      {
        accessorKey: 'location',
        header: 'Location',
        size: 300,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.location,
          helperText: validationErrors?.location,
          onFocus: () => clearValidationError('location'),
        },
      },
      {
        accessorKey: 'zoneCode',
        header: 'Zone Code',
        size: 200,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.zoneCode,
          helperText: validationErrors?.zoneCode,
          onFocus: () => clearValidationError('zoneCode'),
        },
      },
      {
        enableEditing: false,
        accessorKey: 'createdOn',
        header: 'Creation Date',
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return value ? format(new Date(value), 'd-MMM-yyyy') : '-';
        },
      },
    ],
    [validationErrors, clearValidationError]
  );

  // Configure the zone table
  const zoneTable = useMaterialReactTable({
    columns: zoneColumns,
    data: zones || [],
    enableSorting: true,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableEditing: true,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    positionActionsColumn: 'first',
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: (params) => void handleCreateZone(params),
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: (params) => void handleUpdateZone(params),
    state: {
      sorting,
      isLoading: isMutating,
    },
    onSortingChange: setSorting,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 120,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4 text-14'>No zones found</p>,
    renderRowActions,
  });

  // Expose table methods to parent component
  useImperativeHandle(ref, () => ({
    openCreatingRow: () => zoneTable.setCreatingRow(true),
    closeCreatingRow: () => zoneTable.setCreatingRow(null),
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={zoneTable} />

      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteZone(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this zone?</p>
      </ModalConfirm>
    </div>
  );
});

// Set display name for devtools
ZoneDetailsTable.displayName = 'ZoneDetailsTable';
